import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaQuoteLeft } from 'react-icons/fa';
import '../../styles/pages/admin/AdminCommon.scss';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:8000';

function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    quote: '',
    author: '',
    role: '',
    image: null
  });
  
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = () => {
    setLoading(true);
    fetch(`${API_BASE}/api/cms/testimonials`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setTestimonials(data.data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching testimonials:", err);
        setLoading(false);
      });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const resetForm = () => {
    setFormData({ quote: '', author: '', role: '', image: null });
    setImagePreview(null);
    setEditingId(null);
    setShowModal(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEdit = (testimonial) => {
    setFormData({
      quote: testimonial.quote,
      author: testimonial.author,
      role: testimonial.role,
      image: null
    });
    
    if (testimonial.image) {
      setImagePreview(testimonial.image.startsWith('http') ? testimonial.image : `${API_BASE}/${testimonial.image}`);
    } else {
      setImagePreview(null);
    }
    
    setEditingId(testimonial.id);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      fetch(`${API_BASE}/api/cms/testimonials/${id}`, { method: 'DELETE' })
        .then(res => res.json())
        .then(data => {
          if (data.success) fetchTestimonials();
          else alert('Failed to delete');
        })
        .catch(err => console.error(err));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('quote', formData.quote);
    data.append('author', formData.author);
    data.append('role', formData.role);
    if (formData.image) data.append('image', formData.image);

    const url = editingId 
      ? `${API_BASE}/api/cms/testimonials/${editingId}`
      : `${API_BASE}/api/cms/testimonials`;
      
    const method = editingId ? 'PUT' : 'POST';

    fetch(url, { method, body: data })
      .then(res => res.json())
      .then(resData => {
        if (resData.success) {
          fetchTestimonials();
          resetForm();
        } else {
          alert('Failed to save testimonial');
        }
      })
      .catch(err => console.error(err));
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Testimonials</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <FaPlus /> Add Testimonial
        </button>
      </div>

      <div className="content-card">
        {loading ? (
          <p>Loading testimonials...</p>
        ) : testimonials.length === 0 ? (
          <p>No testimonials found. Add some!</p>
        ) : (
          <div className="items-grid">
            {testimonials.map(item => {
              const avatarUrl = item.image 
                ? (item.image.startsWith('http') ? item.image : `${API_BASE}/${item.image}`)
                : `https://ui-avatars.com/api/?name=${encodeURIComponent(item.author)}&background=00526A&color=fff&size=40&bold=true`;
                
              return (
                <div key={item.id} className="item-card">
                  <div className="item-image testimonial-avatar">
                    <img src={avatarUrl} alt={item.author} style={{width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', margin: '0 auto', display: 'block'}} />
                  </div>
                  <div className="item-details" style={{textAlign: 'center'}}>
                    <h3>{item.author}</h3>
                    <span className="text-muted">{item.role}</span>
                    <p style={{fontStyle: 'italic', marginTop: '10px', fontSize: '0.9rem'}}><FaQuoteLeft style={{color: '#ccc', marginRight: '5px'}}/> {item.quote}</p>
                  </div>
                  <div className="item-actions">
                    <button className="btn-icon edit" onClick={() => handleEdit(item)}><FaEdit /></button>
                    <button className="btn-icon delete" onClick={() => handleDelete(item.id)}><FaTrash /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{editingId ? 'Edit Testimonial' : 'Add Testimonial'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Quote / Feedback *</label>
                <textarea 
                  name="quote" 
                  value={formData.quote} 
                  onChange={handleInputChange} 
                  required 
                  rows="4"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Author Name *</label>
                  <input 
                    type="text" 
                    name="author" 
                    value={formData.author} 
                    onChange={handleInputChange} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Role / Position *</label>
                  <input 
                    type="text" 
                    name="role" 
                    value={formData.role} 
                    onChange={handleInputChange} 
                    required 
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Author Avatar (Optional)</label>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageChange}
                  ref={fileInputRef}
                />
                {imagePreview && (
                  <div className="image-preview" style={{marginTop: '10px'}}>
                    <img src={imagePreview} alt="Preview" style={{width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover'}} />
                  </div>
                )}
                <small className="text-muted">If no image is provided, an auto-generated initial avatar will be used.</small>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={resetForm}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Testimonial</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTestimonials;
